import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banner, Pill } from '../ui'
import { NOTIFICATION_TYPES } from '../help/notificationTypes.js'
import {
  dismissNotification,
  listNotifications,
  ON_NOTIFICATION_PUBLISHED,
} from '../api/notifications.js'
import { useSubscription } from '../api/useSubscription.js'

/**
 * Renders the top-most undismissed, non-expired notification targeted at the
 * current user. If multiple match, shows one at a time with a "+N more" hint;
 * dismiss reveals the next. Subscribes to live publishes so new broadcasts
 * appear without a refresh.
 *
 * Activity notifications with a `link` field render the body as a clickable
 * link — clicking navigates to the target page and dismisses the notification.
 */
export function NotificationBannerStack({ me, enabled = true }) {
  const [items, setItems] = useState([])
  const org = me?.org || null
  const navigate = useNavigate()

  const userId = me?.userId || null

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    listNotifications()
      .then((rows) => {
        if (cancelled) return
        setItems(rows.filter((r) =>
          !r.dismissedAt && !r.expired &&
          (!r.targetUserId || r.targetUserId === userId),
        ))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [enabled, userId])

  const handleIncoming = useCallback((data) => {
    const n = data?.onNotificationPublished
    if (!n) return
    if (n.targetUserId && n.targetUserId !== userId) return
    setItems((prev) => {
      if (prev.some((p) => p.id === n.id)) return prev
      return [{ ...n, dismissedAt: null, expired: false }, ...prev]
    })
  }, [userId])

  useSubscription(
    ON_NOTIFICATION_PUBLISHED,
    { targetOrg: org },
    handleIncoming,
    { enabled: enabled && !!org },
  )
  useSubscription(
    ON_NOTIFICATION_PUBLISHED,
    { targetOrg: 'GLOBAL' },
    handleIncoming,
    { enabled },
  )

  const top = items[0]
  const remaining = Math.max(items.length - 1, 0)

  const handleDismiss = useCallback(async () => {
    if (!top) return
    const id = top.id
    setItems((prev) => prev.filter((n) => n.id !== id))
    try { await dismissNotification(id) } catch { /* best-effort */ }
  }, [top])

  const handleLinkClick = useCallback((e) => {
    e.preventDefault()
    if (!top?.link) return
    const id = top.id
    const link = top.link
    setItems((prev) => prev.filter((n) => n.id !== id))
    try { dismissNotification(id) } catch { /* best-effort */ }
    navigate(link)
  }, [top, navigate])

  const tone = useMemo(() => NOTIFICATION_TYPES[top?.type]?.tone || 'info', [top])

  if (!top) return null
  return (
    <Banner tone={tone} dismissible onDismiss={handleDismiss}>
      <strong>{top.title}</strong>
      {top.link ? (
        <a
          href={top.link}
          onClick={handleLinkClick}
          style={{ marginLeft: 8, color: 'inherit', textDecoration: 'underline' }}
        >
          {top.body}
        </a>
      ) : (
        <span style={{ marginLeft: 8 }}>{top.body}</span>
      )}
      {remaining > 0 ? (
        <Pill tone="neutral" style={{ marginLeft: 12 }} aria-label={`${remaining} more notifications`}>
          +{remaining} more
        </Pill>
      ) : null}
    </Banner>
  )
}
